<template>
  <div class="download-page hierarchy-v3 hierarchy-subframe">
    <div class="viewport-fit" :style="viewportStyle">
      <main class="download-center" :style="stageStyle">
        <section ref="matrix" class="matrix" aria-label="系统与下载渠道" :style="flowStyle(0)">
          <perimeter-flow class="matrix-four-flow" :width="820" :height="820" :radius="21" gradient-id="outer-perimeter-spectrum" />

          <aside class="systems">
            <div class="brand-menu next-brand-zone left-brand-zone">
              <a
                class="brand-lockup"
                href="https://universe.top/"
                target="_blank"
                rel="noopener noreferrer"
                data-analytics-id="app_center.official_site.brand"
                aria-label="宇宙之幂首页"
                aria-describedby="brand-profile"
              >
                <span class="brand-mark"><img :src="brandLogo" alt="" draggable="false" width="38" height="38" /></span>
                <h1 class="next-brand-copy"><strong>宇宙之幂</strong><i aria-hidden="true">|</i><b lang="en">UniversePower</b></h1>
              </a>
              <section id="brand-profile" class="brand-profile" aria-label="宇宙之幂品牌简介">
                <span class="brand-profile-corners" aria-hidden="true"><i></i><i></i><i></i><i></i></span>
                <span class="brand-profile-scan" aria-hidden="true"></span>
                <div class="brand-profile-visual">
                  <span class="brand-profile-kicker">UniversePower</span>
                  <img src="/assets/brand/logo-4572.1-256.webp" alt="宇宙之幂公司 Logo" draggable="false" width="112" height="112" />
                  <strong>宇宙之幂</strong>
                </div>
                <div class="brand-profile-content">
                  <h2>宇宙之幂</h2>
                  <p>【宇宙之幂】拥有&lt;私域宇宙&gt;&lt;现实宇宙&gt;&lt;映射宇宙&gt;&lt;平行宇宙&gt;&lt;分类宇宙&gt;&lt;支持宇宙&gt;，并拥有&lt;宇宙形态&gt;&lt;宇宙意识&gt;&lt;宇宙行为&gt;&lt;宇宙力量&gt;&lt;宇宙成品&gt;&lt;宇宙内核&gt;。您可在【宇宙之幂】中快速创造、实现一切目标、产品、愿望。</p>
                  <div class="brand-profile-company">宇宙之幂（深圳）科技集团有限公司</div>
                </div>
              </section>
            </div>

            <div class="system-list" role="tablist" aria-label="操作系统">
              <button
                v-for="(entry, index) in navigationEntries"
                :key="entry.id"
                class="system primary-entry"
                :class="{ active: isEntryActive(entry.id), 'emulator-button': entry.id === 'simulator' }"
                :style="cardStyle(entry, index)"
                type="button"
                role="tab"
                :aria-selected="String(isEntryActive(entry.id))"
                :data-system="entry.id"
                :id="entry.id === 'simulator' ? 'emulator-button' : null"
                :data-analytics-id="'app_center.platform.' + entry.id"
                @click="selectEntry(entry.id)"
              >
                <span v-if="entry.id === 'windows'" class="system-icon windows-icon"><i></i><i></i><i></i><i></i></span>
                <span v-else class="system-icon"><img :class="{ 'local-color-icon': entry.id === 'web' || entry.id === 'mini' }" :src="entry.icon" alt="" /></span>
                <span class="system-copy"><b>{{ entry.label.split('（')[0] }}<span v-if="entry.label.includes('（')" class="release-edition">（{{ entry.label.split('（')[1] }}</span></b><small>{{ entryStatusText(entry) }}</small></span>
                <em aria-hidden="true">→</em>
                <i class="flow-frame flow-sharp" aria-hidden="true"></i>
                <i class="flow-frame flow-glow" aria-hidden="true"></i>
              </button>
            </div>

            <div class="left-access">

              <button
                v-for="(product, index) in productEntries"
                :key="product.id"
                class="product-entry-button primary-entry"
                :class="{ active: activeProductKey === product.id }"
                :style="cardStyle(product, index + 9)"
                type="button"
                :aria-pressed="String(activeProductKey === product.id)"
                :aria-label="`打开${product.label}${product.unitName}选择`"
                :data-analytics-id="'app_center.product.' + product.id"
                @click="selectProduct(product.id)"
              >
                <span class="site-icon product-entry-icon"><img :class="{ 'dynamic-product-image': product.dynamic }" :src="product.icon" alt="" /></span>
                <span class="site-copy"><b>{{ product.label }}</b><small>{{ productStatusText(product) }}</small></span>
                <em aria-hidden="true">→</em>
                <i class="flow-frame flow-sharp" aria-hidden="true"></i>
                <i class="flow-frame flow-glow" aria-hidden="true"></i>
              </button>

              <button
                class="official-site-button"
                :class="{ active: officialFocus }"
                :style="flowStyle(9)"
                type="button"
                aria-label="在右侧全屏查看宇宙之幂官网"
                data-analytics-id="app_center.official_site.preview"
                @click="openOfficialSite"
              >
                <span class="site-icon image-site-icon universe-site-icon"><img :src="brandLogo" alt="宇宙之幂" draggable="false" /></span>
                <span class="site-copy"><b>官网</b><small>宇宙之幂官方网站</small></span>
                <em aria-hidden="true">→</em>
                <i class="flow-frame flow-sharp" aria-hidden="true"></i>
                <i class="flow-frame flow-glow" aria-hidden="true"></i>
              </button>
            </div>
          </aside>

          <section id="resident-download-channels" class="channels" :class="{ 'official-focus': officialFocus }">
            <div
              ref="channelConsole"
              class="channel-console"
              :class="{ 'stores-only': storesOnly, 'store-expanded': !activeProduct && !officialFocus, 'product-area-mode': Boolean(activeProduct), 'official-only': officialOnly }"
              :style="channelConsoleStyle"
            >
              <div ref="channelHeading" class="section-heading channel-heading">
                <span class="channel-title-icon" :data-kind="activeProduct ? `product-${activeProduct.id}` : activeEntryId" aria-hidden="true">
                  <img v-if="activeProduct" :class="{ 'dynamic-product-image': activeProduct.dynamic }" :src="activeProduct.headingIcon" alt="" />
                  <span v-else-if="activeEntryId === 'windows'" class="heading-windows"><i></i><i></i><i></i><i></i></span>
                  <img v-else :class="{ 'mumu-heading-icon': isSimulator, 'local-color-icon': activeEntryId === 'web' || activeEntryId === 'mini' }" :src="activeEntry.icon" alt="" />
                </span>
                <div><b>{{ activeProduct ? activeProduct.label : activeView.title }}</b><small>{{ activeProduct ? activeProduct.english : activeView.subtitle }}</small></div>
                <div v-if="!activeProduct" class="availability" :class="activeView.state"><i></i>{{ activeView.stateText }}</div>
              </div>

              <article v-if="!activeProduct" id="featured" class="featured" :class="{ 'is-actionable': officialOnly }" :style="flowStyle(9)" :role="officialOnly ? 'link' : null" :tabindex="officialOnly ? 0 : -1" @click="handleFeaturedClick" @keydown.enter.self.prevent="handleFeaturedKeyboard" @keydown.space.self.prevent="handleFeaturedKeyboard">
                <div class="featured-orbit" aria-hidden="true"></div>
                <div id="featured-mark" class="market-mark" data-kind="universe-power">
                  <img class="universe-power-download-icon" src="/assets/brand/logo-4572.1-128.webp" alt="" />
                </div>
                <div id="featured-platform-status" class="featured-platform-status">{{ featuredPlatformStatus }}</div>
                <div class="featured-copy">
                  <span>{{ activeView.featuredLabel || (activeView.open ? '官网下载' : '发布方式') }}</span>
                  <h2>{{ activeView.name }}</h2>
                  <p>{{ activeView.description }}</p>
                  <div class="meta"><span v-for="item in activeView.meta" :key="item">{{ item }}</span></div>
                </div>
                <button class="primary-action" type="button" :disabled="!activeView.downloadUrl" :style="flowStyle(10)" :data-analytics-id="'app_center.download.primary.' + activeEntryId" @click="handlePrimaryAction">
                  {{ activeView.action }} <i>{{ activeView.open ? '→' : '·' }}</i>
                  <i class="flow-frame flow-sharp" aria-hidden="true"></i>
                  <i class="flow-frame flow-glow" aria-hidden="true"></i>
                </button>
                <i class="flow-frame flow-sharp" aria-hidden="true"></i>
                <i class="flow-frame flow-glow" aria-hidden="true"></i>
              </article>

              <div v-if="!activeProduct && !officialOnly" id="channel-grid" class="channel-grid" :data-count="String(activeView.channels.length)" :data-mode="isSimulator ? 'simulator' : 'stores'">
                <component
                  v-for="(channel, index) in activeView.channels"
                  :key="activeEntryId + '-' + channel.mark + '-' + index"
                  :is="channelTag(channel)"
                  class="channel-card rich-channel-card"
                  :class="{ open: activeView.open, 'simulator-channel': isSimulator }"
                  :style="channelStyle(channel, index)"
                  :type="channelTag(channel) === 'button' ? 'button' : null"
                  :href="channelTag(channel) === 'a' ? channel.url : null"
                  :target="channelTag(channel) === 'a' ? '_blank' : null"
                  :rel="channelTag(channel) === 'a' ? 'noopener noreferrer' : null"
                  data-hover-click="false"
                  :disabled="!channel.url"
                  :data-analytics-id="'app_center.channel.' + activeEntryId + '.' + (channel.mark || index)"
                  @click="handleChannel($event, channel)"
                >
                  <span class="channel-mark" :class="{ 'system-channel-mark': isSimulator }" :data-kind="isSimulator ? channel.icon : activeEntryId" :data-fallback="channel.mark">
                    <span class="channel-mark-fallback">{{ channel.mark }}</span>
                    <span v-if="channel.icon === 'windows'" class="heading-windows"><i></i><i></i><i></i><i></i></span>
                    <img v-else :src="resolveChannelIcon(channel)" :alt="channel.name + '图标'" loading="lazy" @error="hideBrokenImage" />
                  </span>
                  <b>{{ channel.name }}</b>
                  <small>{{ channel.description }}</small>
                  <span class="channel-kicker">{{ activeView.scope }} · OFFICIAL</span>
                  <em>{{ activeView.open ? (isSimulator ? '官方下载' : '可获取') : '即将开放' }}</em>
                  <span class="channel-watermark" aria-hidden="true"><span>{{ channel.mark }}</span></span>
                  <i class="flow-frame flow-sharp" aria-hidden="true"></i>
                  <i class="flow-frame flow-glow" aria-hidden="true"></i>
                </component>

              </div>

              <footer v-if="!activeProduct" class="channel-footer">
                <div><i></i><span>{{ activeView.note }}</span></div>
                <button type="button" @click="showHelp">安装帮助</button>
              </footer>

              <button
                id="channel-qr-launch"
                class="channel-qr-launch qr-guide-button channel-qr-guide"
                type="button"
                data-analytics-id="app_center.channel_qr.trigger"
                aria-label="展开当前平台下载二维码"
                aria-expanded="false"
                aria-controls="channel-qr-popover"
              >
                <img class="qr-launch-thumb" src="/static/download-single-sphere/universe-power-qr-20260819.png" alt="" />
              </button>
              <section id="channel-qr-popover" class="channel-qr-popover qr-popover button-origin" aria-label="当前平台下载二维码" aria-hidden="true">
                <span class="qr-effect-corners" aria-hidden="true"><i></i><i></i><i></i><i></i></span>
                <span class="qr-effect-scan" aria-hidden="true"></span>
                <span class="focus-qr-frame"><img src="/static/download-single-sphere/universe-power-qr-20260819.png" alt="宇宙之幂下载二维码" /></span>
              </section>

              <section v-if="!activeProduct" class="official-channel-viewer" aria-label="宇宙之幂官网快捷视图">
                <iframe title="宇宙之幂官网快捷视图" :src="officialChannelLoaded ? officialSiteUrl : 'about:blank'" loading="lazy"></iframe>
                <div class="official-channel-actions" aria-label="官网功能区">
                  <button type="button" :style="flowStyle(19)" @click="showOfficialDownloadHint">
                    获取【宇宙之幂】
                    <i class="flow-frame flow-sharp" aria-hidden="true"></i><i class="flow-frame flow-glow" aria-hidden="true"></i>
                  </button>
                  <button type="button" :style="flowStyle(20)" @click="openOfficialExternal">
                    English
                    <i class="flow-frame flow-sharp" aria-hidden="true"></i><i class="flow-frame flow-glow" aria-hidden="true"></i>
                  </button>
                </div>
              </section>

              <section v-else class="product-area-panel" :aria-label="`${activeProduct.label}${activeProduct.unitName}选择`">
                <div class="product-area-grid" role="radiogroup" :aria-label="`${activeProduct.label}${activeProduct.unitName}单选`">
                  <button
                    v-for="area in 100"
                    :key="`${activeProduct.id}-${area}`"
                    class="product-area-card area-visible"
                    type="button"
                    role="radio"
                    :aria-checked="String(selectedProductAreas[activeProduct.id] === area)"
                    :aria-label="`选择 ${area}${activeProduct.unitName}`"
                    :data-area="area"
                    :data-analytics-id="'app_center.product_area.' + activeProduct.id + '.' + area"
                    :style="productCardStyle(activeProduct, area)"
                    @click="selectProductArea(area)"
                  >
                    <img :class="{ 'dynamic-product-image': activeProduct.dynamic }" :src="activeProduct.gridIcon" alt="" draggable="false" loading="lazy" decoding="async" />
                    <strong>{{ area }}{{ activeProduct.unit }}</strong>
                    <span class="product-area-check" aria-hidden="true"></span>
                    <i class="area-double-flow" aria-hidden="true"></i>
                  </button>
                </div>
              </section>
            </div>

            <section ref="experienceStage" class="experience-stage" aria-label="宇宙豆常驻对话区域">
              <button class="universe-bean-chat-trigger download-bean-trigger" type="button" hidden tabindex="-1" aria-hidden="true" aria-controls="bean-chat-overlay"></button>
            </section>
          </section>

          <i class="flow-frame flow-sharp" aria-hidden="true"></i>
          <i class="flow-frame flow-glow" aria-hidden="true"></i>
        </section>
      </main>
    </div>

    <div id="bean-chat-backdrop" class="bean-chat-backdrop" aria-hidden="true"></div>
    <div ref="agentFrameMount" style="display: contents" v-once></div>

    <div class="toast" :class="{ show: toastVisible }" role="status" aria-live="polite">{{ toastMessage }}</div>
  </div>
</template>

<script>
import agentFrameContent from './agent-frame-content'
import '../../homepage-source/menu-motion-manifest'
import androidIcon from './assets/icons/android.svg'
import appleIcon from './assets/icons/apple.svg'
import harmonyIcon from './assets/icons/harmonyos.svg'
import linuxIcon from './assets/icons/linux.svg'
import microsoftStoreIcon from './assets/icons/microsoft-store.svg'
import miniProgramIcon from './assets/icons/mini-program.svg'
import officialIcon from './assets/icons/universe-official-256.png'
import webAccessIcon from './assets/icons/web-access.svg'
import PerimeterFlow from './perimeter-flow'

import downloadCenterModel from './download-center-model'
import downloadCenterFlow from './download-center-flow'
import installStandardDualFlow from './standard-dual-flow'
import installDownloadCenterGuide from './download-center-guide'
import installDownloadUniverseBeanChat from './download-universe-bean-chat-current'

const navigationIcons = {
  android: androidIcon,
  ios: appleIcon,
  harmony: harmonyIcon,
  windows: microsoftStoreIcon,
  macos: appleIcon,
  linux: linuxIcon,
  web: webAccessIcon,
  mini: miniProgramIcon,
  simulator: '/static/download-single-sphere/product-ui/3399.webp'
}

const bundledChannelIcons = {
  'microsoft-store': microsoftStoreIcon,
  'web-access': webAccessIcon
}

const productEntries = [
  { id: 'photon', label: '宇宙光子', subtitle: '宇宙级光子空间', english: 'UNIVERSE PHOTON', unit: '㎡', unitName: '平方米', accent: '#62efff' },
  { id: 'wall', label: '宇宙之璧', subtitle: '宇宙级墙壁', assetName: '宇宙墙壁', english: 'UNIVERSE WALL', unit: '㎡', unitName: '平方米', accent: '#ff9b4f' },
  { id: 'display', label: '宇宙超显', subtitle: '宇宙级超级显示屏', english: 'UNIVERSE HYPERDISPLAY', unit: '㎡', unitName: '平方米', accent: '#71ffbf' },
  { id: 'silicon', label: '宇宙硅脑', subtitle: '宇宙级硅基大脑', english: 'UNIVERSE SILICON BRAIN', unit: '台', unitName: '台', accent: '#b86cff' },
  { id: 'immersive', label: '宇宙沉浸', subtitle: '台数规格', english: 'UNIVERSE IMMERSION', unit: '台', unitName: '台', accent: '#ff9b4f', icon: '/static/download-single-sphere/product-ui/宇宙沉浸.webp' },
  { id: 'wearable', label: '宇宙穿戴', subtitle: '台数规格', english: 'UNIVERSE WEARABLE', unit: '台', unitName: '台', accent: '#ff76bc', icon: '/static/download-single-sphere/product-ui/宇宙穿戴.webp' }
].map(product => {
  const assetName = product.assetName || product.label
  const icon = product.icon || `/static/download-single-sphere/product-ui/${assetName}.webp`
  const motionSource = ['immersive', 'wearable'].includes(product.id)
    ? `/static/download-single-sphere/product-ui/${assetName}.webp`
    : `/download-center-embed/assets/optimized/product-ui/${assetName}.webp`
  const headingMotion = window.MenuMotionManifest[motionSource]
  return Object.assign({}, product, {
    icon,
    motionSource,
    headingIcon: headingMotion ? headingMotion.poster : icon,
    gridIcon: product.gridIcon || icon
  })
})

export default {
  name: 'DownloadCenter',
  components: { PerimeterFlow },
  data () {
    const initialMetrics = downloadCenterModel.getStageMetrics(
      typeof window === 'undefined' ? 820 : window.innerWidth,
      typeof window === 'undefined' ? 820 : window.innerHeight
    )
    const initialExperiencePolicy = downloadCenterModel.getExperiencePolicy(
      typeof window === 'undefined' ? 820 : window.innerWidth,
      typeof window === 'undefined' ? 820 : window.innerHeight
    )
    const navigationEntries = downloadCenterModel.getNavigationEntries().map(entry => {
      return Object.assign({}, entry, { icon: navigationIcons[entry.id] })
    })

    return {
      downloadRevision: 0,
      activeEntryId: 'android',
      activeProductKey: '',
      brandLogo: '/assets/brand/logo-4572.1-128.webp',
      experiencePolicy: initialExperiencePolicy,
      flowRenderer: null,
      keydownHandler: null,
      navigationEntries,
      officialChannelLoaded: false,
      officialFocus: false,
      officialIcon,
      officialSiteLoaded: false,
      officialSiteUrl: 'https://universe.top/',
      qrOpen: false,
      qrOrigin: 'sphere',
      qrHideTimer: null,
      resizeHandler: null,
      selectedProductAreas: { photon: null, wall: null, display: null, silicon: null, immersive: null, wearable: null },
      stageMetrics: initialMetrics,
      toastMessage: '',
      toastTimer: null,
      toastVisible: false,
      stopStandardDualFlow: null,
      stopGuide: null,
      stopUniverseBeanChat: null
    }
  },
  computed: {
    activeEntry () {
      return this.navigationEntries.find(entry => entry.id === this.activeEntryId)
    },
    activeView () {
      const revision = this.downloadRevision
      const base = downloadCenterModel.getView(this.activeEntryId)
      const catalog = window.AppCenterDownloads || {}
      const record = catalog[this.activeEntryId === 'simulator' ? 'windowsPreview' : this.activeEntryId] || {}
      const channelKeys = { ios: ['ios', 'iosTestFlight'], harmony: ['harmony', 'harmonyService'], windows: ['windowsStore'], macos: ['macosStore'], linux: ['linuxSnap', 'linuxFlathub'], mini: ['wechatMini', 'alipayMini', 'douyinMini', 'baiduMini'] }
      const channels = base.channels.map((channel, index) => {
        const item = catalog[(channelKeys[this.activeEntryId] || [])[index]] || {}
        return Object.assign({}, channel, { url: item.enabled ? item.downloadUrl : '' })
      })
      const downloadUrl = record.enabled ? record.downloadUrl : ''
      const open = Boolean(downloadUrl || channels.some(channel => channel.url))
      return Object.assign({}, base, { channels, downloadUrl, open, state: open ? 'open' : base.state, stateText: open ? '已开放' : base.stateText, action: downloadUrl ? '下载 / 打开' : '暂未开放', catalogRevision: revision })
    },
    activeProduct () {
      return productEntries.find(product => product.id === this.activeProductKey) || null
    },
    channelConsoleStyle () {
      if (this.activeProduct || this.officialFocus) return {}
      const rowCount = this.isSimulator ? 2 : Math.max(1, Math.ceil(this.activeView.channels.length / 2))
      // 304 是 0827 方案里下载大框的设计高度（official-package-schemes.css 的同名兜底值）：
      // 主卡拿到 304 - 34 标题行 = 240，第一行才放得下 148px 的球体。给 270 会把球体挤出卡片顶边。
      const height = this.isSimulator
        ? 270
        : Math.max(304, 117 + rowCount * 48 + Math.max(0, rowCount - 1) * 4)
      return {
        '--store-row-count': String(rowCount),
        '--store-expanded-height': `${height}px`
      }
    },
    featuredPlatformStatus () {
      const view = this.activeView
      const label = this.activeEntry ? this.activeEntry.label : ''
      return `${label}端下载 · ${view.stateText}`
    },
    officialOnly () {
      return !this.activeProduct && !this.officialFocus && (this.activeEntryId === 'android' || this.isSimulator)
    },
    isSimulator () {
      return this.activeEntryId === 'simulator'
    },
    platformEntries () {
      return this.navigationEntries.filter(entry => entry.kind === 'platform')
    },
    productEntries () {
      return productEntries
    },
    simulatorEntry () {
      return this.navigationEntries.find(entry => entry.kind === 'simulator')
    },
    stageStyle () {
      return { transform: `scale(${this.stageMetrics.scale})` }
    },
    storesOnly () {
      return this.activeEntryId === 'ios' || this.activeEntryId === 'harmony'
    },
    viewportStyle () {
      return {
        width: `${this.stageMetrics.width}px`,
        height: `${this.stageMetrics.height}px`
      }
    }
  },
  mounted () {
    const frame = document.createElement('universe-agent-frame')
    frame.setAttribute('mode', 'inline')
    frame.innerHTML = agentFrameContent
    this.$refs.agentFrameMount.appendChild(frame)

    this.downloadCatalogHandler = () => { this.downloadRevision++ }
    window.addEventListener('download-catalog:updated', this.downloadCatalogHandler)
    document.body.classList.add('hierarchy-v3', 'hierarchy-subframe', 'bean-resident-layout')
    this.setHierarchyFocus(this.activeEntryId, this.activeEntry.accent)
    this.resizeHandler = () => {
      this.stageMetrics = downloadCenterModel.getStageMetrics(window.innerWidth, window.innerHeight)
      this.experiencePolicy = downloadCenterModel.getExperiencePolicy(window.innerWidth, window.innerHeight)
      if (this.experiencePolicy.autoplayOfficialPreview) {
        this.officialSiteLoaded = true
      }
      this.refreshFlow()
    }
    this.resizeHandler()
    window.addEventListener('resize', this.resizeHandler, { passive: true })
    this.officialSiteLoaded = this.experiencePolicy.autoplayOfficialPreview
    this.keydownHandler = event => {
      if (event.key === 'Escape') this.closeQr()
    }
    document.addEventListener('keydown', this.keydownHandler)
    this.$nextTick(() => {
      this.initializeFlow()
      // 标准 Canvas 双流光会自己清掉旧的 .flow-frame 图层，必须在首屏渲染完成后再装。
      this.stopStandardDualFlow = installStandardDualFlow({ animate: true })
      if (window.OpeningCopy) window.OpeningCopy.application(this.activeEntryId)
      this.stopUniverseBeanChat = installDownloadUniverseBeanChat(this.$el)
      this.stopGuide = installDownloadCenterGuide()
    })
  },
  beforeDestroy () {
    window.removeEventListener('download-catalog:updated', this.downloadCatalogHandler)
    window.removeEventListener('resize', this.resizeHandler)
    document.body.classList.remove('hierarchy-v3', 'hierarchy-subframe', 'bean-resident-layout')
    delete document.body.dataset.hierarchyFocus
    document.body.style.removeProperty('--hierarchy-accent')
    document.removeEventListener('keydown', this.keydownHandler)
    if (this.flowRenderer) this.flowRenderer.destroy()
    if (this.stopStandardDualFlow) this.stopStandardDualFlow()
    if (this.stopGuide) this.stopGuide()
    if (this.stopUniverseBeanChat) this.stopUniverseBeanChat()
    document.documentElement.classList.remove('official-package-standard')
    clearTimeout(this.toastTimer)
    clearTimeout(this.qrHideTimer)
  },
  methods: {
    cardStyle (entry, index) {
      return Object.assign({ '--icon-accent': entry.accent }, downloadCenterModel.getFlowStyle(index))
    },
    channelStyle (channel, index) {
      return Object.assign(
        { '--accent': channel.accent },
        this.flowStyle(index + 11)
      )
    },
    channelTag (channel) {
      return this.activeView.open && channel.url ? 'a' : 'button'
    },
    entryStatusText (entry) {
      return entry.stateText
    },
    flowStyle (index) {
      return downloadCenterModel.getFlowStyle(index)
    },
    perimeterFlowStyle (index) {
      return downloadCenterModel.getPerimeterFlowStyle(index)
    },
    productStatusText (product) {
      return downloadCenterModel.getProductOrderStatus(product.id)
    },
    handleChannel (event, channel) {
      if (!channel.url) {
        if (event) event.preventDefault()
        return
      }
      this.trackBusinessEvent(channel.url ? 'download_clickout' : 'feature_trigger', {
        feature: channel.url ? 'download_channel' : 'market_search_hint',
        app_id: 'universe_power',
        platform: this.activeEntryId,
        channel: channel.mark || channel.name,
        download_type: channel.url ? 'external_link' : 'market_search'
      })
      if (channel.url) return
      if (event) event.preventDefault()
      this.notify(`${channel.name}：请在该应用市场搜索【宇宙之幂】并完成下载。`)
    },
    handleFeaturedClick (event) {
      if (!this.officialOnly || event.target.closest('button, a')) return
      this.handlePrimaryAction()
    },
    handleFeaturedKeyboard () {
      if (this.officialOnly) this.handlePrimaryAction()
    },
    handlePrimaryAction () {
      if (!this.activeView.downloadUrl) return
      this.trackBusinessEvent('feature_trigger', {
        feature: 'official_site_open',
        app_id: 'universe_power',
        platform: this.activeEntryId,
        channel: 'primary',
        download_type: 'official_site'
      })
      this.openExternal(this.activeView.downloadUrl)
    },
    hideBrokenImage (event) {
      event.target.style.display = 'none'
    },
    closeQr () {
      clearTimeout(this.qrHideTimer)
      this.qrHideTimer = null
      this.qrOpen = false
    },
    // 鼠标移入按钮或弹层时取消待执行的收起，方便从按钮滑到二维码上。
    holdQr () {
      clearTimeout(this.qrHideTimer)
      this.qrHideTimer = null
    },
    // 鼠标滑出后短暂延时再收起，避免按钮与弹层之间的空隙导致闪烁。
    scheduleCloseQr () {
      if (!this.qrOpen) return
      clearTimeout(this.qrHideTimer)
      this.qrHideTimer = setTimeout(() => {
        this.qrHideTimer = null
        this.qrOpen = false
      }, 180)
    },
    initializeFlow () {
      if (!this.$refs.matrix || this.flowRenderer) return
      this.flowRenderer = downloadCenterFlow.createDownloadCenterFlowRenderer({
        matrix: this.$refs.matrix,
        channelPanel: this.$refs.channelConsole,
        experienceStage: this.$refs.experienceStage,
        channelHeading: this.$refs.channelHeading,
        getActiveSource: () => this.$el.querySelector('.primary-entry.active, .official-site-button.active')
      })
    },
    isEntryActive (entryId) {
      /* 打开某个产品时也要把系统/模拟器的 active 摘掉：关联线的起点是
         `querySelector('.primary-entry.active, .official-site-button.active')`，
         按文档顺序取第一个命中。系统在产品之前，只要它还留着 active，
         线就永远从系统那一行出发，产品这几行看起来"没有连线"。
         嵌入页的 renderProductArea() 同样会先清掉 .system.active。 */
      return !this.officialFocus && !this.activeProductKey && this.activeEntryId === entryId
    },
    notify (message) {
      clearTimeout(this.toastTimer)
      this.toastMessage = message
      this.toastVisible = true
      this.toastTimer = setTimeout(() => {
        this.toastVisible = false
      }, 2500)
    },
    openExternal (url) {
      /* 用一次性 <a> 而不是 window.open：window.open 的第三个参数会被浏览器
         当成"窗口特征串"，于是按弹窗处理 —— 轻则新开一个独立窗口而不是标签页，
         重则被弹窗拦截，表现就是"点了要等一下才跳"。
         <a target=_blank rel="noopener noreferrer"> 是同一次用户手势里的普通
         导航，不会被拦截也没有等待。嵌入页 扫码后下载中心_系统渠道联动.js 的
         openExternal() 是同一份实现。 */
      if (!url) return
      const link = document.createElement('a')
      link.href = url
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      link.style.display = 'none'
      link.dataset.analyticsIgnore = 'true'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    openOfficialExternal () {
      this.openExternal(this.officialSiteUrl)
    },
    openOfficialSite () {
      this.activeProductKey = ''
      this.officialFocus = true
      this.setHierarchyFocus('official', '#70ffb0')
      this.officialChannelLoaded = true
      this.officialSiteLoaded = true
      this.refreshFlow()
    },
    refreshFlow () {
      this.$nextTick(() => {
        if (this.flowRenderer) this.flowRenderer.refresh()
      })
    },
    openQr (origin = 'sphere') {
      clearTimeout(this.qrHideTimer)
      this.qrHideTimer = null
      if (this.qrOpen && this.qrOrigin === origin) return
      this.qrOpen = false
      this.qrOrigin = origin
      this.$nextTick(() => {
        this.qrOpen = true
      })
    },
    productCardStyle (product, area) {
      const seed = [...product.id].reduce((value, character) => ((value * 33) ^ character.charCodeAt(0)) >>> 0, 2166136261)
      const hueA = Math.round((seed % 360 + (area - 1) * 137.508) % 360)
      const hueB = Math.round((hueA + 47 + ((area - 1) * 83 % 246)) % 360)
      const speed = (4.85 + ((seed + area * 97) % 436) / 100).toFixed(2)
      return {
        '--product-accent-a': `hsl(${hueA} 97% 64%)`,
        '--product-accent-b': `hsl(${hueB} 97% 64%)`,
        '--product-flow-speed': `${speed}s`,
        '--product-flow-delay': `${-((area * 0.37) % Number(speed)).toFixed(2)}s`
      }
    },
    resolveChannelIcon (channel) {
      if (channel.icon === 'macos') return appleIcon
      return bundledChannelIcons[channel.icon] || channel.icon
    },
    selectEntry (entryId) {
      // The standalone application center owns its message; avoid briefly painting the legacy copy.
      if (window.OpeningCopy && !window.AppCenterPlatformIntro) window.OpeningCopy.application(entryId)
      this.activeProductKey = ''
      this.officialFocus = false
      this.activeEntryId = entryId
      const entry = this.navigationEntries.find(item => item.id === entryId)
      this.setHierarchyFocus(entryId, entry ? entry.accent : '#52efff')
      this.refreshFlow()
    },
    selectProduct (productId) {
      if (window.OpeningCopy && !window.AppCenterPlatformIntro) window.OpeningCopy.application(productId)
      this.activeProductKey = productId
      this.officialFocus = false
      const product = productEntries.find(item => item.id === productId)
      this.setHierarchyFocus(`product-${productId}`, product ? product.accent : '#52efff')
      this.closeQr()
      this.refreshFlow()
    },
    selectProductArea (area) {
      if (!this.activeProduct) return
      this.$set(this.selectedProductAreas, this.activeProduct.id, area)
    },
    showHelp () {
      this.notify('下载完成后，请根据系统提示完成安装。')
    },
    showOfficialDownloadHint () {
      this.notify('请扫描左侧二维码获取【宇宙之幂】官方版本。')
    },
    trackBusinessEvent (eventName, properties) {
      if (!window.SiteAnalytics) return
      window.SiteAnalytics.track(eventName, Object.assign({
        surface: 'app_center'
      }, properties))
    },
    setHierarchyFocus (focus, accent) {
      document.body.dataset.hierarchyFocus = focus
      document.body.style.setProperty('--hierarchy-accent', accent)
    },
    toggleQr (origin = 'button') {
      if (this.qrOpen && this.qrOrigin === origin) {
        this.closeQr()
        return
      }
      this.openQr(origin)
    }
  }
}
</script>

<style src="./styles/matrix-base.css"></style>
<style src="./styles/black-stage.css"></style>
<style src="./styles/hierarchy-v3.css"></style>
<style src="./styles/subframe.css"></style>
<style src="./styles/download-center.css"></style>
<style src="./styles/single-sphere.css"></style>
<style src="./styles/official-package-schemes.css"></style>
<style src="./styles/download-center-0827.css"></style>
<style src="./styles/channel-card-click-only.css"></style>
<style src="../../static/universe-bean-chat.css"></style>
<style src="./styles/download-universe-bean-chat.css"></style>
<style src="../../homepage-source/智体紧凑对话框.css"></style>
<style src="./styles/download-reference-corrections.css"></style>
<style src="./styles/current-universe-bean-sync.css"></style>

<style src="../../homepage-source/download-center-embed/resident-bean-layout.css"></style>

<style src="../../homepage-source/download-center-embed/official-download-only.css"></style>



// WEBPACK FOOTER //
// src/download/DownloadCenter.vue